import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Pencil, Settings } from '@factorialco/f0-react/icons/app'
import {
  GoalsDataCollectionDocument,
  GoalsDataCollectionQuery,
  PerformanceGoalsUpdateMetricGroupFragment,
  PerformanceCyclesCycleFilterDocument,
} from 'generated/resources/graphql'
import { sortBy } from 'lodash'

import buildUrl from 'modules/core/lib/buildUrl'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'

import { UpdateMetricGroupModal } from 'modules/goals/components/UpdateMetricGroupModal'
import { NO_CYCLE_OPTION } from 'modules/performance_cycles/components/CycleFilter'

type GoalsDataCollectionNode = NonNullable<
  GoalsDataCollectionQuery['goals']['metricGroupsConnection']
>['edges'][number]['node']

type CycleFilterNode = {
  id: string
  title: string
  [key: string]: unknown
}

type GoalsFiltersStorage = {
  cycle?: string[]
}

type GoalStatus = {
  label: string
  status: 'positive' | 'neutral' | 'negative' | 'warning'
}

function toStringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return []
  return v.filter((x): x is string => typeof x === 'string')
}

function canUpdateMetricGroup(item: GoalsDataCollectionNode): boolean {
  return (item._policies?.update?.can ?? false) === true
}

function getGoalStatus(node: GoalsDataCollectionNode): GoalStatus {
  const progress = node.summary?.averageProgress ?? 0
  const needsAttention = node.summary?.needsAttention ?? false

  if (progress >= 100) {
    return { label: i18n.t('goals_revamp.goals_list.status.completed'), status: 'positive' }
  }
  if (needsAttention) {
    return { label: i18n.t('goals_revamp.goals_list.status.at_risk'), status: 'warning' }
  }
  if (progress > 0) {
    return { label: i18n.t('goals_revamp.goals_list.status.in_progress'), status: 'neutral' }
  }
  return { label: i18n.t('goals_revamp.goals_list.status.not_started'), status: 'neutral' }
}

interface GoalsDataCollectionProps {
  canCreateMetricGroup: boolean
  canManageCycles: boolean
  onCreate: () => void
  onManageCycles: () => void
}

const TABLE_COLUMNS = [
  {
    key: 'goal',
    label: () => i18n.t('goals_revamp.goals_list.columns.goal'),
    render: (item: GoalsDataCollectionNode) => ({
      type: 'text' as const,
      value: item.title ?? '',
    }),
  },
  {
    key: 'assigned_to',
    label: () => i18n.t('goals_revamp.goals_list.columns.assigned_to'),
    render: (item: GoalsDataCollectionNode) => {
      const assignees = item.assigneesConnection?.edges ?? []
      if (assignees.length === 0) {
        return { type: 'text' as const, value: { placeholder: '-' } }
      }
      return {
        type: 'avatarList' as const,
        value: {
          avatarList: assignees.map(({ node: assignee }) => ({
            type: 'person' as const,
            firstName: assignee.firstName,
            lastName: assignee.lastName,
            src: assignee.access?.avatar ? buildUrl(assignee.access.avatar) : undefined,
          })),
          max: 5,
        },
      }
    },
  },
  {
    key: 'cycle',
    label: () => i18n.t('goals_revamp.goals_list.columns.cycle'),
    render: (item: GoalsDataCollectionNode) => {
      const cycle = item.performanceCycles?.[0]
      return { type: 'text' as const, value: cycle?.title ?? { placeholder: '-' } }
    },
  },
  {
    key: 'progress',
    label: () => i18n.t('goals_revamp.goals_list.columns.progress'),
    align: 'right' as const,
    render: (item: GoalsDataCollectionNode) => {
      const progress = Math.min(item.summary?.averageProgress ?? 0, 100)
      return {
        type: 'progressBar' as const,
        value: {
          value: progress,
          max: 100,
          label: `${i18n.formatNumber(progress)}%`,
        },
      }
    },
  },
  {
    key: 'status',
    label: () => i18n.t('goals_revamp.goals_list.columns.status'),
    render: (item: GoalsDataCollectionNode) => {
      const status = getGoalStatus(item)
      return { type: 'status' as const, value: { label: status.label, status: status.status } }
    },
  },
  {
    key: 'due_date',
    label: () => i18n.t('goals_revamp.goals_list.columns.due_date'),
    render: (item: GoalsDataCollectionNode) => {
      const endOn = item.performanceCycles?.[0]?.endOn
      return { type: 'date' as const, value: endOn ? new Date(endOn) : undefined }
    },
  },
]

export const GoalsDataCollection: React.FC<GoalsDataCollectionProps> = ({
  canCreateMetricGroup,
  canManageCycles,
  onCreate,
  onManageCycles,
}) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [editingMetricGroup, setEditingMetricGroup] =
    useState<PerformanceGoalsUpdateMetricGroupFragment | null>(null)

  const [savedFilters, setSavedFilters] = useLocalStorage<GoalsFiltersStorage>(
    LOCAL_STORAGE_KEYS.goalsGroupFilters,
    {}
  )

  const cycleFilterSource = useGraphqlDataSource(
    {
      document: PerformanceCyclesCycleFilterDocument,
      fetchPolicy: 'cache-first',
      perPage: 200,
      mapData: (data): CycleFilterNode[] => {
        const sortedCycles = sortBy(data.performanceCycles.cycles ?? [], ['title'])

        return [
          {
            id: NO_CYCLE_OPTION,
            title: i18n.t('performance_cycles.cycle_filter.no_cycle_option'),
          },
          ...sortedCycles.map((cycle) => ({ ...cycle, title: cycle.title ?? '' })),
        ]
      },
      mapPagination: (data) => ({
        total: (data.performanceCycles.cycles?.length ?? 0) + 1,
      }),
      mapVariables: () => ({}),
    },
    []
  )

  const source = useGraphqlDataCollectionSource<GoalsDataCollectionNode>({
    document: GoalsDataCollectionDocument,
    mapData: (data) => {
      const edges: GoalsDataCollectionQuery['goals']['metricGroupsConnection']['edges'] =
        data.goals.metricGroupsConnection?.edges ?? []
      return edges.map((edge) => edge.node).filter(Boolean)
    },
    mapVariables: ({ search: searchTerm, filters }) => {
      const cycleValues = toStringArray(filters?.cycle)
      const hasNoCycle = cycleValues.includes(NO_CYCLE_OPTION)
      const cycleIds = cycleValues.filter((v) => v !== NO_CYCLE_OPTION)

      setSavedFilters({ cycle: cycleValues.length ? cycleValues : undefined })

      return {
        cycleIds: cycleIds.length ? cycleIds : undefined,
        noCycle: hasNoCycle,
        search: searchTerm ?? undefined,
      }
    },
    mapPagination: (data) => {
      const conn = data.goals.metricGroupsConnection
      const pageInfo = conn?.pageInfo
      const totalCount = conn?.totalCount ?? 0
      const edges = conn?.edges ?? []
      const lastEdge = edges[edges.length - 1]

      return {
        total: totalCount,
        cursor: lastEdge?.cursor ?? pageInfo?.endCursor,
        hasNextPage: pageInfo?.hasNextPage ?? false,
      }
    },
    paginationType: 'infinite-scroll',
    perPage: 25,
    totalItemSummary: (totals) => i18n.tp('goals_revamp.goals_list.total_items', { count: totals }),
    itemOnClick: (item) => () => {
      navigate(
        navigation.goals.groups.group.fullPath({
          id: item.id.toString(),
        })
      )
    },
    itemActions: (item) => {
      if (!canUpdateMetricGroup(item)) return undefined

      return [
        {
          label: i18n.t('goals_revamp.goals_list.actions.edit'),
          icon: Pencil,
          onClick: () => setEditingMetricGroup(item),
        },
      ]
    },
    primaryActions: () =>
      canCreateMetricGroup
        ? {
            label: i18n.t('goals_revamp.goals_list.create_button'),
            icon: Add,
            onClick: onCreate,
          }
        : undefined,
    secondaryActions: () =>
      canManageCycles
        ? [
            {
              label: i18n.t('performance_goals.cycles.manage_cycles'),
              icon: Settings,
              onClick: onManageCycles,
            },
          ]
        : undefined,
    search: {
      enabled: true,
    },
    currentFilters: savedFilters ?? {},
    filters: {
      cycle: {
        type: 'in',
        label: i18n.t('performance_cycles.cycle_filter.label'),
        options: {
          source: cycleFilterSource,
          mapOptions: (cycle: CycleFilterNode) => ({
            label: cycle.title,
            value: cycle.id,
          }),
          cache: true,
        },
      },
    },
  })

  return (
    <>
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: TABLE_COLUMNS.map((col) => ({
                ...(col.align && { align: col.align }),
                label: col.label(),
                render: col.render,
              })),
            },
          },
        ]}
      />
      {editingMetricGroup && (
        <UpdateMetricGroupModal
          metricGroup={editingMetricGroup}
          onClose={() => setEditingMetricGroup(null)}
          refetchQueries={[GoalsDataCollectionDocument]}
        />
      )}
    </>
  )
}
